<template>
  <transition name="cx-toast-fade">
    <div v-if="visible" class="cx-toast-overlay">
      <div class="cx-toast-container">
        <div class="cx-toast" @click="openConversation">
          <button
            type="button"
            class="cx-toast__close"
            :aria-label="CX_TOAST_DISMISS_LABEL"
            @click.stop="dismiss"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M12 4L4 12"
                stroke="#202125"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4 4L12 12"
                stroke="#202125"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div class="cx-toast__body">
            <div class="cx-toast__label">
              <span class="cx-toast__dot" />
              <span class="cx-toast__label-text">{{ label }}</span>
            </div>
            <div class="cx-toast__message">{{ message }}</div>
            <div class="cx-toast__time">{{ time }}</div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import _ from 'lodash';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import CashflowAssistantApi from '@/api/CashflowAssistant';
import {
  CX_TEAM_LABEL,
  CX_TOAST_ATTACHMENT_MESSAGE,
  CX_TOAST_FALLBACK_MESSAGE,
  CX_TOAST_DISMISS_LABEL,
  MESSAGE_ROLES,
} from '@/constants/chat';

// The socket event is a pointer — it carries no message text — so the body is
// read over the authenticated API, same as the chat itself does.
export default {
  name: 'CxMessageToast',
  data() {
    return {
      visible: false,
      surfacedFor: null,
      conversationId: null,
      label: CX_TEAM_LABEL,
      message: '',
      time: '',
      CX_TOAST_DISMISS_LABEL,
    };
  },
  computed: {
    inlineCxEnabled() {
      return this.$store.getters['featureFlags/riseupChatCxInline'];
    },
    openConversationId() {
      return this.$store.state.riseupChat.openConversationId;
    },
  },
  watch: {
    '$store.state.websocket.chatEvent': function onChatEvent(event) {
      this.handleChatEvent(event);
    },
    // Loaded asynchronously after boot, so the unread state usually arrives
    // after this is already mounted.
    '$store.state.riseupChat.unreadConversationId': function onUnread(conversationId) {
      this.surfaceUnread(conversationId);
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.surfaceUnread(this.$store.state.riseupChat.unreadConversationId);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    // The socket stops reconnecting for good after three failed attempts, so a
    // tab that slept through a websockets deploy never hears another push.
    // Returning to the tab reconnects and re-derives unread from the server —
    // the same self-healing the chat itself does on visibilitychange.
    onVisibilityChange() {
      if (document.visibilityState !== 'visible' || !this.inlineCxEnabled) { return; }
      if (this.$socket && !this.$socket.connected) {
        this.$socket.connect();
      }
      this.$store.dispatch('riseupChat/refreshUnread');
    },
    // A reply that arrived while the customer was away never produces a live
    // push, so without this it would only ever be a dot. Surfaced once per
    // conversation: the dot is what persists until it is actually read.
    surfaceUnread(conversationId) {
      if (!conversationId) {
        // Read (or no unread): the next unread conversation may surface again.
        this.surfacedFor = null;
        return;
      }
      if (!this.inlineCxEnabled || conversationId === this.surfacedFor) { return; }
      if (conversationId === this.openConversationId) { return; }
      this.surfacedFor = conversationId;
      this.present(conversationId);
    },
    async handleChatEvent(event) {
      if (!this.inlineCxEnabled || _.get(event, 'type') !== 'cx-message') { return; }
      const conversationId = _.get(event, 'conversationId');
      // Already on screen in the chat itself, so a toast would be noise.
      if (!conversationId || conversationId === this.openConversationId) { return; }
      await this.present(conversationId);
    },
    // The text is an enhancement, not a precondition: failing to read it must
    // not swallow the notification that the team replied.
    async present(conversationId) {
      const reply = await this.fetchLatestCxReply(conversationId);
      this.conversationId = conversationId;
      this.label = _.get(reply, 'authorName') ? `${reply.authorName} ${CX_TEAM_LABEL}` : CX_TEAM_LABEL;
      this.message = _.get(reply, 'text') || CX_TOAST_FALLBACK_MESSAGE;
      this.time = _.get(reply, 'time') || '';
      this.show();
    },
    async fetchLatestCxReply(conversationId) {
      try {
        const data = await CashflowAssistantApi.getConversationMessages(conversationId);
        const last = _.findLast(data.messages, m => m.role === MESSAGE_ROLES.CX);
        if (!last) { return null; }
        const textBlock = _.find(last.blocks, b => b.type === 'text' && _.trim(b.value));
        return {
          authorName: last.authorName || null,
          // An image-only reply has nothing to quote, so it is described instead.
          text: textBlock ? textBlock.value : CX_TOAST_ATTACHMENT_MESSAGE,
          time: this.formatTime(last.createdAt),
        };
      } catch (error) {
        DDLogs.error('[inline-cx] Failed to read the reply for the toast', { error });
        return null;
      }
    },
    formatTime(createdAt) {
      if (!createdAt) { return ''; }
      const date = new Date(createdAt);
      if (Number.isNaN(date.getTime())) { return ''; }
      const day = `${date.getDate()}/${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`;
      const clock = `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
      return `${day}, ${clock}`;
    },
    // Stays until dismissed or opened: Figma gives it a close button, and a
    // reply that vanishes on a timer is one the customer can miss entirely.
    show() {
      this.visible = true;
      Segment.trackUserInteraction('AIChatbot_CxReplyToastShown', { conversationId: this.conversationId });
    },
    dismiss() {
      this.visible = false;
      Segment.trackUserInteraction('AIChatbot_CxReplyToastDismissed', { conversationId: this.conversationId });
    },
    async openConversation() {
      const { conversationId } = this;
      this.visible = false;
      Segment.trackUserInteraction('AIChatbot_CxReplyToastClicked', { conversationId });
      const { default: openAssistant } = await import('@/chat-boot/assistantLauncher');
      openAssistant({ initiator: 'cx_reply_toast', conversationId });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-z-index';

/* Pinned to the app frame's bottom edge, immune to whichever container
   scrolls. The offsets mirror ResponsivePage's frame CSS (5vh top margin;
   55px = footer + card margin) — if that frame math changes, change this. */
.cx-toast-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  pointer-events: none;
  z-index: $toast-message;
}

.cx-toast-container {
  box-sizing: border-box;
  width: 100%;
  max-width: 400px;
  padding: 0 17px;
  margin-bottom: 20px;
  pointer-events: auto;

  @media (min-width: 460px) {
    margin-bottom: calc(100vh * 0.05 + 55px + 20px);
  }
}

/* Figma: intercom in RiseUp Chat, node 17092:76316. White rather than the
   in-chat cream because it sits over page content; the 40px left padding
   reserves the close button's column, which Figma gets from a fixed body. */
.cx-toast {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 13px 27px 15px 40px;
  border: 1px solid rgba(0, 0, 0, 0.03);
  border-radius: 20px;
  background-color: #ffffff;
  box-shadow: 0 4px 12.5px rgba(32, 33, 37, 0.19);
  cursor: pointer;
}

/* Figma puts the 16px lucide/x at 20,15 from the card's corner; the button box
   is larger for the finger, negative-margined so the icon stays on that spot. */
.cx-toast__close {
  position: absolute;
  top: 15px;
  left: 20px;
  width: 32px;
  height: 32px;
  margin: -8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.55;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
}

/* RTL: flex-start is the RIGHT edge, which is where Figma aligns the label,
   message and timestamp alike. flex-end here would shove short rows left. */
.cx-toast__body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  min-width: 0;
  direction: rtl;
}

/* The dot leads the label in reading order, so it sits to its right. */
.cx-toast__label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cx-toast__label-text {
  font-size: 14px;
  line-height: 20px;
  color: #202125;
  opacity: 0.45;
}

.cx-toast__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #fecf3a;
  flex-shrink: 0;
}

/* Figma keeps this on one line; real replies are longer, so it clamps. */
.cx-toast__message {
  width: 100%;
  font-size: 20px;
  line-height: 26px;
  color: #202125;
  text-align: right;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.cx-toast__time {
  font-size: 14px;
  line-height: 20px;
  color: #202125;
  opacity: 0.45;
}

.cx-toast-fade-enter-active,
.cx-toast-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.cx-toast-fade-enter,
.cx-toast-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
